import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function (...args) {
    const later = () => {
      timeout = null
      func.apply(this, args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

class MediaImage {
  /**
   * @param {HTMLElement} element - The container element for the image.
   */
  constructor(element) {
    if (!element) return
    this.element = element
    this.image = element.querySelector('img')
    if (!this.image) return
    this.init()
  }

  /**
   * Initializes the MediaImage instance, sets up observers and event listeners.
   */
  init() {
    this.storeOriginalSrc()

    // Cache picture and sources for performance
    this.picture = this.image.closest('picture')
    this.sources = this.picture ? this.picture.querySelectorAll('source') : []

    // Cache element existence checks
    this.hasImage = !!this.image
    this.hasMobileSrc = !!(this.image?.dataset?.srcmobile)

    this.checkAndApplyMobileImage()

    // Debounce the resize handler for performance
    this.debouncedResize = debounce(this.handleResize.bind(this), 150)
    window.addEventListener('resize', this.debouncedResize, { passive: true })

    this.setupMutationObserver()

    if (this.image.loading === 'lazy' || this.image.decoding === 'async') {
      this.intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.setEagerImage(this.image)
          }
        })
        this.intersectionObserver.unobserve(this.image)
      })

      this.intersectionObserver.observe(this.image)
    }
  }

  /**
   * Stores the original image src for later restoration.
   */
  storeOriginalSrc() {
    const currentSrc = this.image?.getAttribute('src')
    const isMobile = window.innerWidth < GLOBAL_CONSTANTS.BREAKPOINTS.mobile

    if (isMobile) {
      this.originalSrc = this.getDesktopSrc() || this.image?.dataset?.src || currentSrc
    } else {
      this.originalSrc = currentSrc
    }
  }

  /**
   * Sets up a MutationObserver to watch for src changes.
   */
  setupMutationObserver() {
    if (this.hasMobileSrc) {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'src') {
            this.checkAndApplyMobileImage()
          }
        })
      })
      this.mutationObserver.observe(this.image, { attributes: true, attributeFilter: ['src'] })
    }
  }

  /**
   * Handles window resize events (debounced).
   */
  handleResize = () => {
    this.checkAndApplyMobileImage()
  }

  /**
   * Checks and applies the appropriate image for mobile/desktop.
   */
  checkAndApplyMobileImage() {
    if (!this.hasMobileSrc) return

    const isMobile = window.innerWidth < GLOBAL_CONSTANTS.BREAKPOINTS.mobile
    const currentSrc = this.image?.getAttribute('src')
    const mobileSrc = this.image?.dataset?.srcmobile

    if (isMobile && currentSrc !== mobileSrc) {
      this.image?.setAttribute('src', mobileSrc)
    } else if (!isMobile && currentSrc === mobileSrc) {
      if (this.originalSrc) {
        this.image?.setAttribute('src', this.originalSrc)
      } else {
        const desktopSrc = this.getDesktopSrc()
        if (desktopSrc) {
          this.image?.setAttribute('src', desktopSrc)
        }
      }
    }
  }

  /**
   * Gets the desktop image src from the <picture> element or data attributes.
   * @returns {string|null}
   */
  getDesktopSrc() {
    // Prefer data-src if present
    if (this.image?.dataset?.src) {
      return this.image.dataset.src
    }
    // Prefer <source> without media attribute
    if (this.picture) {
      const desktopSource = this.picture.querySelector('source:not([media])')
      if (desktopSource && desktopSource.srcset) {
        const firstSrc = desktopSource.srcset.split(',')[0]?.split(' ')[0]
        if (firstSrc) return firstSrc
      }
    }
    // Fallback to data-desktopsrc
    return this.image?.dataset?.desktopsrc || null
  }

  /**
   * Sets the image to eager loading and preloads the appropriate source.
   * @param {HTMLImageElement} image
   */
  setEagerImage(image) {
    image.loading = 'eager'
    image.decoding = 'sync'

    const isMobileNow = window.innerWidth < GLOBAL_CONSTANTS.BREAKPOINTS.mobile
    let source
    if (!this.sources || this.sources.length === 0) {
      return
    } else if (this.sources.length === 1) {
      source = this.sources[0]
    } else {
      if (isMobileNow && this.picture.querySelector('source[media]')) {
        source = this.picture.querySelector('source[media]')
      } else {
        source = this.picture.querySelector('source:not([media])')
      }
    }

    if (!source) {
      return
    }

    // Determine the src for the preload link
    let preloadSrc
    if (isMobileNow && this.hasMobileSrc) {
      preloadSrc = image.dataset.srcmobile
    } else {
      preloadSrc = image.src
    }

    // Check for existing preload link with same src and imagesrcset
    const selector = `link[rel='preload'][as='image'][src='${preloadSrc}']` + (source.srcset ? `[imagesrcset='${source.srcset}']` : '')
    if (document.head.querySelector(selector)) {
      return // Duplicate found, do not add another
    }

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.setAttribute('src', preloadSrc)
    if (source.srcset) {
      link.setAttribute('imagesrcset', source.srcset)
    }
    if (source.sizes) {
      link.setAttribute('imagesizes', source.sizes)
    }
    document.head.appendChild(link)
  }

  /**
   * Cleans up event listeners and observers for this instance.
   */
  destroy() {
    window.removeEventListener('resize', this.debouncedResize)
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }
}

export default {
  selector: '.js-media-image',
  Source: MediaImage
}
